// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { BadgeCheck, Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type Firmware,
  type FirmwareSortBy,
  type OtaCpuType,
  type OtaDeviceType,
  useFirmwareListQuery,
} from "@/api/hooks/use-firmware";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { MemoryStickPlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Button } from "@/components/ui/button";
import { CreateFirmwareDialog } from "@/features/hardware/create-firmware-dialog";
import { FirmwareActionsMenu } from "@/features/hardware/firmware-actions-menu";
import {
  FirmwareCpuTypeBadge,
  FirmwareDeviceTypeBadge,
  FirmwareFirmwareTypeBadge,
  FirmwareProductionBadge,
} from "@/features/hardware/firmware-badges";
import { FirmwareToggleDialog, type FirmwareToggleTarget } from "@/features/hardware/firmware-toggle-dialog";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/hardware/firmware/index";

const DEVICE_TYPES: OtaDeviceType[] = ["esp32", "stridingcam", "sc25"];
const CPU_TYPES: OtaCpuType[] = ["esp32", "t41"];

const FILTER_DEFAULTS = {
  search: "",
  deviceType: "",
  cpuType: "",
  onlyProduction: true,
  onlyActive: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "versionNum",
  sortDesc: true,
};

export function FirmwareList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.hardwareFirmwareEdit);
  const [toggleTarget, setToggleTarget] = useState<FirmwareToggleTarget | null>(null);
  const [toggleField, setToggleField] = useState<"active" | "production">("active");
  const [createOpen, setCreateOpen] = useState(false);
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [deviceType, setDeviceType] = useState(initial.deviceType);
  const [cpuType, setCpuType] = useState(initial.cpuType);
  const [onlyProduction, setOnlyProduction] = useState(initial.onlyProduction);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);

  const sortBy = (sorting[0]?.id ?? "versionNum") as FirmwareSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);
  // Production + active are on by default, so a non-default state means the toggle is OFF.
  const isFiltered = !!(search || deviceType || cpuType || !onlyProduction || !onlyActive);

  const clearFilters = useCallback(() => {
    setSearch("");
    setDeviceType("");
    setCpuType("");
    setOnlyProduction(true);
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: createOpen || toggleTarget !== null });

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      deviceType,
      cpuType,
      onlyProduction,
      onlyActive,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "versionNum",
      sortDesc: sorting[0]?.desc ?? true,
    }),
    [search, deviceType, cpuType, onlyProduction, onlyActive, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(() => {
    const encoded = encodeFilters(currentFilters, FILTER_DEFAULTS);
    navigate({ to: "/hardware/firmware", search: encoded ? { f: encoded } : {}, replace: true });
  }, [currentFilters, navigate]);

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setDeviceType(next.deviceType);
    setCpuType(next.cpuType);
    setOnlyProduction(next.onlyProduction);
    setOnlyActive(next.onlyActive);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useFirmwareListQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    deviceType: (deviceType || undefined) as OtaDeviceType | undefined,
    cpuType: (cpuType || undefined) as OtaCpuType | undefined,
    onlyProduction: onlyProduction || undefined,
    onlyActive: onlyActive || undefined,
  });

  const rows = useMemo<Firmware[]>(() => data?.firmware ?? [], [data?.firmware]);

  const columns = useMemo<DataTableColumnDef<Firmware>[]>(
    () => [
      {
        accessorKey: "firmwareId",
        header: t("firmware.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("firmware.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.firmwareId} />,
      },
      {
        accessorKey: "versionStr",
        header: t("firmware.list.columns.version"),
        // Not server-sortable (the API's sort enum has no versionStr; sorting the display string
        // alphabetically would mis-order anyway). Use the numeric "Build" (versionNum) column for
        // version ordering. See CIN-663.
        enableSorting: false,
        meta: { columnLabel: t("firmware.list.columns.version") },
        cell: ({ row }) => (
          <Link
            to="/hardware/firmware/$firmwareId"
            params={{ firmwareId: String(row.original.firmwareId) }}
            className="font-mono font-medium text-primary hover:underline w-fit"
          >
            {row.original.versionStr}
          </Link>
        ),
      },
      {
        accessorKey: "versionNum",
        header: t("firmware.list.columns.versionNum"),
        size: 100,
        meta: { columnLabel: t("firmware.list.columns.versionNum") },
        cell: ({ row }) => <span className="font-mono tabular-nums">{row.original.versionNum}</span>,
      },
      {
        accessorKey: "deviceType",
        header: t("firmware.list.columns.deviceType"),
        size: 130,
        meta: { columnLabel: t("firmware.list.columns.deviceType") },
        cell: ({ row }) => <FirmwareDeviceTypeBadge deviceType={row.original.deviceType} />,
      },
      {
        accessorKey: "cpuType",
        header: t("firmware.list.columns.cpuType"),
        size: 100,
        meta: { columnLabel: t("firmware.list.columns.cpuType") },
        cell: ({ row }) => <FirmwareCpuTypeBadge cpuType={row.original.cpuType} />,
      },
      {
        accessorKey: "firmwareType",
        header: t("firmware.list.columns.firmwareType"),
        size: 110,
        enableSorting: false,
        meta: { columnLabel: t("firmware.list.columns.firmwareType") },
        cell: ({ row }) => <FirmwareFirmwareTypeBadge firmwareType={row.original.firmwareType} />,
      },
      {
        id: "isProduction",
        accessorKey: "isProduction",
        header: t("firmware.list.columns.production"),
        size: 110,
        enableSorting: false,
        meta: { columnLabel: t("firmware.list.columns.production") },
        cell: ({ row }) => <FirmwareProductionBadge isProduction={row.original.isProduction} />,
      },
      {
        accessorKey: "createdAt",
        header: t("firmware.list.columns.createdAt"),
        meta: { columnLabel: t("firmware.list.columns.createdAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
      {
        id: "isActive",
        header: t("firmware.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("firmware.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      ...(canEdit
        ? [
            {
              id: "actions",
              size: 50,
              meta: { forceWidth: true, sticky: "right" },
              enableSorting: false,
              enableHiding: false,
              cell: ({ row }: { row: { original: Firmware } }) => (
                <FirmwareActionsMenu
                  isActive={row.original.isActive}
                  isProduction={row.original.isProduction}
                  onToggleActive={() => {
                    setToggleField("active");
                    setToggleTarget(toTarget(row.original));
                  }}
                  onToggleProduction={() => {
                    setToggleField("production");
                    setToggleTarget(toTarget(row.original));
                  }}
                />
              ),
            } satisfies DataTableColumnDef<Firmware>,
          ]
        : []),
    ],
    [t, canEdit],
  );

  return (
    <div>
      <PageHeader title={t("firmware.title")} />
      <div className="p-6">
        <DataTable
          storageKey="firmware-list"
          columns={columns}
          data={rows}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          searchPlaceholder={t("firmware.list.searchPlaceholder")}
          presets={
            <FilterPresets
              storageKey="firmware-list"
              defaults={FILTER_DEFAULTS}
              current={currentFilters}
              onApply={applyPreset}
            />
          }
          filters={
            <>
              <FilterSelect
                label={t("firmware.list.filter.deviceType")}
                value={deviceType}
                onChange={(v) => {
                  setDeviceType(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={DEVICE_TYPES.map((d) => ({ label: d, value: d }))}
                searchPlaceholder={t("firmware.list.filter.searchDeviceTypes")}
                emptyText={t("firmware.list.filter.noDeviceTypes")}
              />
              <FilterSelect
                label={t("firmware.list.filter.cpuType")}
                value={cpuType}
                onChange={(v) => {
                  setCpuType(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={CPU_TYPES.map((c) => ({ label: c, value: c }))}
                searchPlaceholder={t("firmware.list.filter.searchCpuTypes")}
                emptyText={t("firmware.list.filter.noCpuTypes")}
              />
              <FilterToggle
                active={onlyProduction}
                onChange={() => {
                  setOnlyProduction((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={BadgeCheck}
                label={t("firmware.list.filter.production")}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            canEdit ? (
              <Button onClick={() => setCreateOpen(true)}>
                <MemoryStickPlusIcon className="h-4 w-4" />
                {t("firmware.list.addButton")}
              </Button>
            ) : null
          }
        />
      </div>

      <CreateFirmwareDialog open={createOpen} onOpenChange={setCreateOpen} />

      <FirmwareToggleDialog target={toggleTarget} field={toggleField} onClose={() => setToggleTarget(null)} />
    </div>
  );
}

function toTarget(v: Firmware): FirmwareToggleTarget {
  return {
    firmwareId: v.firmwareId,
    versionStr: v.versionStr,
    isActive: v.isActive,
    isProduction: v.isProduction,
  };
}
